import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Star, Plus, X, GripVertical, RefreshCw, Loader2, TrendingUp, TrendingDown, ChevronDown, ChevronLeft } from "lucide-react";

const LS_KEY = "qtr-fav-watchlist";
const LS_COLLAPSED = "qtr-fav-watchlist-collapsed";
const DEFAULT_FAVS = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD", "ETHUSD", "NAS100", "SPX500", "USDCAD", "AUDUSD"];
const MAX_FAVS = 20;
const REFRESH_MS = 30000;

function loadFavs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length) return arr.slice(0, MAX_FAVS);
    }
  } catch { /* noop */ }
  return DEFAULT_FAVS;
}

function saveFavs(list) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch { /* noop */ }
}

/**
 * Floating watchlist widget — anchored bottom-right of the dashboard.
 * - Holds up to 20 favorite assets, persisted in localStorage.
 * - Live pricing via /api/watchlist/quotes refreshed every 30s.
 * - Clicking an asset calls `onOpenSymbol(symbol)` which opens it in a new
 *   Excel-style sheet on the Dashboard.
 */
export default function FloatingWatchlist({ onOpenSymbol, catalog = {} }) {
  const [favs, setFavs] = useState(loadFavs);
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_COLLAPSED) !== "0"; } catch { return true; }
  });
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState("");
  // Sliding drawer state for viewports < 1536px (< 2xl). On 2xl+ we keep the classic
  // bottom-right floating widget; below 2xl we render a handle on the right edge + drawer.
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleCollapsed = () => setCollapsed((v) => {
    const n = !v;
    try { localStorage.setItem(LS_COLLAPSED, n ? "1" : "0"); } catch { /* noop */ }
    return n;
  });

  const flatSymbols = useMemo(() => {
    const all = [];
    Object.values(catalog).forEach((list) => list.forEach((s) => all.push(s)));
    return Array.from(new Set(all)).sort();
  }, [catalog]);

  const suggestions = useMemo(() => {
    if (!query.trim()) return flatSymbols.filter((s) => !favs.includes(s)).slice(0, 20);
    const q = query.trim().toUpperCase();
    return flatSymbols.filter((s) => s.includes(q) && !favs.includes(s)).slice(0, 20);
  }, [flatSymbols, favs, query]);

  const refreshQuotes = async (list = favs) => {
    if (!list.length) { setQuotes({}); return; }
    setLoading(true);
    try {
      const { items } = await api.watchlistQuotes(list, "H1");
      const map = {};
      (items || []).forEach((it) => { map[it.symbol] = it; });
      setQuotes(map);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => {
    refreshQuotes(favs);
    const id = setInterval(() => refreshQuotes(favs), REFRESH_MS);
    return () => clearInterval(id);
  }, [favs.join(",")]);

  const addFav = (symbol) => {
    if (favs.includes(symbol) || favs.length >= MAX_FAVS) return;
    const next = [...favs, symbol];
    setFavs(next); saveFavs(next);
    setQuery("");
  };
  const removeFav = (symbol) => {
    const next = favs.filter((s) => s !== symbol);
    setFavs(next); saveFavs(next);
  };
  const moveFav = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= favs.length || to >= favs.length) return;
    const next = [...favs];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setFavs(next); saveFavs(next);
  };

  // Drag-and-drop reorder handlers
  const dragIdxRef = React.useRef(null);
  const onDragStart = (idx) => (e) => { dragIdxRef.current = idx; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (idx) => (e) => { e.preventDefault(); moveFav(dragIdxRef.current, idx); dragIdxRef.current = null; };

  const decimals = (sym, price) => {
    if (price == null) return "—";
    if (/^BTC|^ETH|^NAS|^SPX|^DJI/.test(sym)) return price.toFixed(2);
    if (/JPY/.test(sym)) return price.toFixed(3);
    return price.toFixed(5);
  };

  const headerBar = (
    <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/[0.02] shrink-0">
      <button
        type="button"
        onClick={toggleCollapsed}
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/70 hover:text-white"
        data-testid="fav-watchlist-toggle"
        title={collapsed ? "Ouvrir" : "Réduire"}
      >
        <Star size={13} className="text-amber-400 fill-amber-400/40" />
        Favoris
        <span className="text-[10px] font-normal text-white/40">({favs.length})</span>
        <ChevronDown size={12} className={`transition-transform ${collapsed ? "-rotate-90" : ""}`} />
      </button>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => refreshQuotes(favs)}
          className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white"
          title="Actualiser"
          data-testid="fav-watchlist-refresh"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        </button>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className={`p-1 rounded hover:bg-white/10 ${showAdd ? "text-accent-blue" : "text-white/60 hover:text-white"}`}
          title="Ajouter un actif"
          data-testid="fav-watchlist-add-btn"
        >
          <Plus size={13} />
        </button>
        {/* Close-drawer button — only meaningful in the mobile/tablet slide-in variant */}
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="2xl:hidden p-1 rounded hover:bg-white/10 text-white/60 hover:text-white"
          title="Fermer"
          data-testid="fav-watchlist-drawer-close"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );

  const panelBody = !collapsed && (
    <>
      {showAdd && (
        <div className="p-2 border-b border-white/10 bg-white/[0.02]" data-testid="fav-watchlist-add-panel">
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher (EURUSD, BTC…)"
            className="w-full text-xs px-2 py-1.5 rounded bg-black/40 border border-white/10 focus:border-accent-blue outline-none placeholder-white/30"
            data-testid="fav-watchlist-search-input"
          />
          <div className="max-h-40 overflow-y-auto mt-1.5 space-y-0.5" data-testid="fav-watchlist-suggestions">
            {favs.length >= MAX_FAVS && (
              <div className="text-[10px] text-warn px-1 py-0.5">Limite atteinte ({MAX_FAVS} max)</div>
            )}
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addFav(s)}
                disabled={favs.length >= MAX_FAVS}
                className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-accent-blue/15 hover:text-accent-blue disabled:opacity-40 disabled:cursor-not-allowed font-mono"
                data-testid={`fav-watchlist-suggest-${s}`}
              >
                {s}
              </button>
            ))}
            {suggestions.length === 0 && (
              <div className="text-[10px] text-white/40 px-1 py-0.5">Aucun résultat.</div>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0" data-testid="fav-watchlist-list">
        {favs.length === 0 && (
          <div className="p-4 text-center text-[11px] text-white/40">
            Aucun favori. Cliquez sur <Plus size={11} className="inline" /> pour en ajouter.
          </div>
        )}
        {favs.map((sym, idx) => {
          const q = quotes[sym];
          const change = q?.change_pct;
          const isUp = change != null && change > 0;
          const isDown = change != null && change < 0;
          return (
            <div
              key={sym}
              draggable
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={onDrop(idx)}
              className="group flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 border-b border-white/[0.03] last:border-b-0"
              data-testid={`fav-watchlist-row-${sym}`}
            >
              <GripVertical size={11} className="text-white/20 group-hover:text-white/50 cursor-grab shrink-0" />
              <button
                type="button"
                onClick={() => { onOpenSymbol?.(sym); setDrawerOpen(false); }}
                className="flex-1 min-w-0 text-left"
                data-testid={`fav-watchlist-open-${sym}`}
                title={`Ouvrir ${sym} dans un nouvel onglet`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11.5px] font-semibold text-white truncate">{sym}</span>
                  <span className="font-mono text-[11px] text-white/80 tabular-nums">
                    {decimals(sym, q?.price)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[9px] uppercase tracking-wider text-white/30">
                    {q?.source === "demo" ? "demo" : q?.source || ""}
                  </span>
                  {change != null && (
                    <span className={`inline-flex items-center gap-0.5 font-mono text-[10px] tabular-nums ${isUp ? "text-up" : isDown ? "text-down" : "text-white/50"}`}>
                      {isUp && <TrendingUp size={9} />}
                      {isDown && <TrendingDown size={9} />}
                      {change > 0 ? "+" : ""}{change.toFixed(2)}%
                    </span>
                  )}
                </div>
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFav(sym); }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-white/40 hover:text-down hover:bg-down/10 shrink-0"
                title="Retirer"
                data-testid={`fav-watchlist-remove-${sym}`}
              >
                <X size={11} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="px-2 py-1 border-t border-white/10 text-[9px] text-white/30 text-center bg-white/[0.02]">
        Cliquez → nouvel onglet · glissez pour réordonner
      </div>
    </>
  );

  return (
    <>
      {/* --- Desktop variant (≥1536px / 2xl) — floating bottom-right widget --- */}
      <div
        className={`hidden 2xl:flex fixed z-[60] bottom-4 right-4 w-[260px] max-h-[70vh] flex-col bg-[#0b1420]/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl overflow-hidden transition-all ${collapsed ? "h-auto" : ""}`}
        data-testid="floating-watchlist"
      >
        {headerBar}
        {panelBody}
      </div>

      {/* --- Mobile/tablet variant (<1536px) — slide-in drawer + edge handle --- */}
      {/* Handle: small tab pinned to the right edge, always visible when drawer closed */}
      {!drawerOpen && (
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="2xl:hidden fixed z-[60] right-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2 py-3 rounded-l-lg bg-[#0b1420]/95 backdrop-blur-md border border-r-0 border-white/10 shadow-xl text-xs font-semibold uppercase tracking-widest text-white/70 hover:text-white hover:bg-[#12233a]/95 transition-colors"
          data-testid="fav-watchlist-drawer-handle"
          title="Ouvrir la watchlist"
        >
          <ChevronLeft size={13} />
          <div className="flex flex-col items-center gap-0.5 [writing-mode:vertical-rl] rotate-180">
            <Star size={11} className="text-amber-400 fill-amber-400/40" />
            <span className="text-[10px]">Favoris ({favs.length})</span>
          </div>
        </button>
      )}
      {/* Backdrop dim on open */}
      {drawerOpen && (
        <div
          className="2xl:hidden fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
          data-testid="fav-watchlist-drawer-backdrop"
        />
      )}
      {/* Drawer panel */}
      <div
        className={`2xl:hidden fixed z-[60] right-0 top-0 bottom-0 w-[280px] flex flex-col bg-[#0b1420]/98 backdrop-blur-md border-l border-white/10 shadow-2xl transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="floating-watchlist-drawer"
        aria-hidden={!drawerOpen}
      >
        {headerBar}
        {panelBody}
      </div>
    </>
  );
}
